/** Shared terrain surface shader; physical ground remains domain87-owned.
 * The industrial coast clips only the decorative land surface over open sea. */
export function createSkyGroundMaterial(T, landMap) {
  return new T.ShaderMaterial({
    transparent: true,
    uniforms: {
      time: { value: 0 },
      base: { value: new T.Color('#295658') },
      accent: { value: new T.Color('#8ec5bd') },
      warm: { value: 0 },
      industrialHaze: { value: 0 },
      shorelineZ: { value: 10000 },
      coastVisibility: { value: 1 },
      landMap: { value: landMap },
    },
    vertexShader: `varying vec3 p; varying vec3 worldPoint; varying vec3 surfaceNormal; void main(){p=position;worldPoint=(modelMatrix*vec4(position,1.)).xyz;surfaceNormal=normalize(mat3(modelMatrix)*normal);gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}`,
    fragmentShader: `varying vec3 p;varying vec3 worldPoint;varying vec3 surfaceNormal;uniform float industrialHaze;uniform float shorelineZ;uniform float coastVisibility;uniform float time;uniform vec3 base;uniform vec3 accent;uniform float warm;uniform sampler2D landMap;
      float hash(vec2 v){return fract(sin(dot(v,vec2(127.1,311.7)))*43758.5453);}
      float noise(vec2 v){vec2 i=floor(v),f=fract(v);f=f*f*(3.-2.*f);return mix(mix(hash(i),hash(i+vec2(1,0)),f.x),mix(hash(i+vec2(0,1)),hash(i+1.),f.x),f.y);}
      void main(){if(worldPoint.z>shorelineZ)discard;vec2 q=vec2(p.x,p.y+time);
      float broad=noise(q*.09),detail=noise(q*1.2);
      vec3 stone=texture2D(landMap,q*.085).rgb;
      vec3 c=mix(base,accent,smoothstep(.25,.85,broad))*(.65+stone*.55);
      c*=.87+detail*.26;
      c*=.55+.57*max(0.,dot(normalize(surfaceNormal),normalize(vec3(-.4,.85,.35))));
      float river=abs(sin(q.x*.095+noise(vec2(q.y*.018,0.))*1.8));
      float channel=(1.-smoothstep(.025,.07,river))*smoothstep(19.,23.,abs(q.x))*(1.-smoothstep(.6,1.3,p.z));
      vec3 water=vec3(.07,.22,.23)+pow(max(0.,sin(q.y*2.6)),18.)*.035;
      c=mix(c,water,channel*(1.-warm*.8));
      float coast=1.-step(9999.,shorelineZ);
      float fade=mix(1.-smoothstep(55.,145.,p.y+70.),1.,coast);
      float mist=industrialHaze*(1.-exp(-distance(cameraPosition,worldPoint)*.055));
      c=mix(c,vec3(.65,.48,.29),mist*.7);gl_FragColor=vec4(c,fade*.99*(1.-mist*(1.-coast*coastVisibility)));}`,
  });
}

/** Ocean sheet sits at the canonical sea plane. Animated light/reflection
 * changes neither water height nor collision. The same coastline clips land
 * and sea, so no road or dry meadow floats across the starting sea corridor. */
export function createSkySeaMaterial(T) {
  return new T.ShaderMaterial({
    uniforms: { time: { value: 0 }, travel: { value: 0 }, shorelineZ: { value: -150 } },
    vertexShader: `varying vec3 p;void main(){p=(modelMatrix*vec4(position,1.)).xyz;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}`,
    fragmentShader: `varying vec3 p;uniform float time;uniform float travel;uniform float shorelineZ;
    void main(){if(p.z<shorelineZ)discard;vec2 q=vec2(p.x,p.z-travel);
    float waves=sin(q.x*.19+q.y*.37+sin(q.x*.07)*2.+time*.8)*sin(q.y*.13-q.x*.21-time*.5);
    float detail=sin(q.x*.71-q.y*.63+time*.3)*sin(q.x*.53+q.y*.81-time*.6);
    float glint=pow(max(0.,waves*detail),22.);
    float haze=1.-exp(-distance(cameraPosition,p)*.004);
    vec3 c=vec3(.025,.14,.21)+waves*.018+detail*.009;
    c+=vec3(.5,.45,.27)*glint*.24;
    c=mix(c,vec3(.38,.49,.53),haze*.58);
    gl_FragColor=vec4(c,1.);}`,
  });
}
